import React, { useMemo } from "react";
import { computeAffordability } from "../../util/affordability";

export interface AffordabilityCalculatorProps {
  price: number;
  rentFrequency: string;
  deposit: number | null;
  postcode: string;
  bedrooms: number | null;
  internalArea: number | null;
  description: string[];
  householdIncome: number;
  incomeIsAnnual: boolean;
  occupants: number;
  includeBills: boolean;
  includeTvLicence: boolean;
  includeContentsInsurance: boolean;
  onIncomeChange: (value: number) => void;
  onIncomePeriodChange: (annual: boolean) => void;
  onOccupantsChange: (value: number) => void;
  onIncludeBillsChange: (value: boolean) => void;
  onIncludeTvLicenceChange: (value: boolean) => void;
  onIncludeContentsInsuranceChange: (value: boolean) => void;
}

const formatGbp = (n: number) =>
  `£${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

const AffordabilityCalculator: React.FC<AffordabilityCalculatorProps> = ({
  price,
  rentFrequency,
  deposit,
  postcode,
  bedrooms,
  internalArea,
  description,
  householdIncome,
  incomeIsAnnual,
  occupants,
  includeBills,
  includeTvLicence,
  includeContentsInsurance,
  onIncomeChange,
  onIncomePeriodChange,
  onOccupantsChange,
  onIncludeBillsChange,
  onIncludeTvLicenceChange,
  onIncludeContentsInsuranceChange,
}) => {
  const monthlyIncome = incomeIsAnnual
    ? householdIncome / 12
    : householdIncome;

  const result = useMemo(
    () =>
      computeAffordability({
        price,
        rentFrequency,
        deposit,
        householdIncomeMonthly: monthlyIncome,
        occupants,
        includeBills,
        postcode,
        bedrooms,
        internalArea,
        description,
        includeTvLicence,
        includeContentsInsurance,
      }),
    [
      price,
      rentFrequency,
      deposit,
      monthlyIncome,
      occupants,
      includeBills,
      postcode,
      bedrooms,
      internalArea,
      description,
      includeTvLicence,
      includeContentsInsurance,
    ]
  );

  const ratioPct =
    result.affordabilityRatio != null
      ? Math.round(result.affordabilityRatio * 100)
      : null;

  const ratioColor =
    result.affordabilityLevel === "red"
      ? "text-red-600"
      : result.affordabilityLevel === "amber"
        ? "text-amber-600"
        : result.affordabilityLevel === "ok"
          ? "text-green-700"
          : "text-text-slate";

  return (
    <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft p-6">
      <div className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-2">
        Tenant Affordability
      </div>
      <h3 className="font-teko text-2xl text-text-obsidian mb-4">
        Can you afford this home?
      </h3>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block font-manrope text-sm text-text-slate mb-2">
            Household income
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              min={0}
              value={householdIncome || ""}
              onChange={(e) => onIncomeChange(Number(e.target.value) || 0)}
              className="flex-1 p-3 border border-solid border-[rgba(0,0,0,0.1)] bg-white text-text-obsidian font-manrope"
              placeholder="e.g. 45000"
            />
            <select
              value={incomeIsAnnual ? "annual" : "monthly"}
              onChange={(e) =>
                onIncomePeriodChange(e.target.value === "annual")
              }
              className="p-3 border border-solid border-[rgba(0,0,0,0.1)] font-manrope bg-white text-text-obsidian"
            >
              <option value="annual">Annual</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block font-manrope text-sm text-text-slate mb-2">
            Occupants
          </label>
          <input
            type="number"
            min={1}
            max={8}
            value={occupants}
            onChange={(e) =>
              onOccupantsChange(Math.max(1, Number(e.target.value) || 1))
            }
            className="w-full p-3 border border-solid border-[rgba(0,0,0,0.1)] bg-white text-text-obsidian font-manrope"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-8 text-sm font-manrope">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeBills}
            onChange={(e) => onIncludeBillsChange(e.target.checked)}
          />
          Include bills estimate
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeTvLicence}
            onChange={(e) => onIncludeTvLicenceChange(e.target.checked)}
          />
          TV licence
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeContentsInsurance}
            onChange={(e) =>
              onIncludeContentsInsuranceChange(e.target.checked)
            }
          />
          Contents insurance
        </label>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="p-4 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
          <div className="text-xs uppercase tracking-wider text-text-slate">
            Move-in cost
          </div>
          <div className="font-teko text-2xl text-text-obsidian mt-1">
            {formatGbp(result.moveInCost)}
          </div>
          <div className="text-xs text-text-grey mt-1">
            Deposit {formatGbp(result.deposit)}
            {result.depositIsEstimate ? " (est.)" : ""} + 1st month
          </div>
        </div>
        <div className="p-4 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
          <div className="text-xs uppercase tracking-wider text-text-slate">
            Monthly total
          </div>
          <div className="font-teko text-2xl text-text-obsidian mt-1">
            {formatGbp(result.monthlyTotal)}
          </div>
          <div className="text-xs text-text-grey mt-1">
            Rent {formatGbp(result.monthlyRent)}
            {includeBills ? ` + bills ${formatGbp(result.monthlyBills)}` : ""}
          </div>
        </div>
        <div className="p-4 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
          <div className="text-xs uppercase tracking-wider text-text-slate">
            Affordability
          </div>
          <div className={`font-teko text-2xl mt-1 ${ratioColor}`}>
            {ratioPct != null ? `${ratioPct}%` : "—"}
          </div>
          <div className="text-xs text-text-grey mt-1">
            {ratioPct == null
              ? "Enter income to calculate"
              : ratioPct > 40
                ? "Above 40% — may be unaffordable"
                : ratioPct > 30
                  ? "Above 30% — consider carefully"
                  : "Within typical guidance"}
          </div>
        </div>
      </div>

      {includeBills && (
        <div className="border-t border-t-solid border-t-[rgba(0,0,0,0.06)] pt-6">
          <div className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-3">
            Estimated monthly bills
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            {[
              ["Council tax", result.councilTaxMonthly],
              ["Utilities", result.utilitiesMonthly],
              ["Broadband", result.broadbandMonthly],
              ["TV licence", result.tvLicenceMonthly],
              ["Contents insurance", result.contentsInsuranceMonthly],
            ].map(([label, amount]) =>
              Number(amount) > 0 ? (
                <div
                  key={String(label)}
                  className="flex justify-between p-3 border border-solid border-[rgba(0,0,0,0.06)]"
                >
                  <span>{label}</span>
                  <span className="font-medium">
                    {formatGbp(Number(amount))}
                    <span className="text-text-grey text-xs ml-1">est.</span>
                  </span>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-text-grey mt-6">
        Indicative only — not financial advice. See the estimate details below
        for sources and accuracy.
      </p>
    </div>
  );
};

export default AffordabilityCalculator;
